/**
 * PurchaseCurrencyModal Component
 * Modal for selecting payment currency when purchasing assets
 */

import { useState } from 'react';

interface PurchaseCurrencyModalProps {
  isOpen: boolean;
  onClose: () => void;
  assetName: string;
  priceUsdt: number;
  priceStars: number;
  usdtBalance: number;
  onPurchase: (currency: 'usdt' | 'stars') => void;
  allowStars?: boolean; // Allow Telegram Stars payment (default: true)
}

export function PurchaseCurrencyModal({
  isOpen,
  onClose,
  assetName,
  priceUsdt,
  priceStars,
  usdtBalance,
  onPurchase,
  allowStars = true,
}: PurchaseCurrencyModalProps) {
  const [selectedCurrency, setSelectedCurrency] = useState<'usdt' | 'stars'>('usdt');

  if (!isOpen) return null;

  const canAffordUsdt = usdtBalance >= priceUsdt;

  const handlePurchase = () => {
    onPurchase(selectedCurrency);
    onClose();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
    }}>
      <div style={{
        background: 'linear-gradient(180deg, #1a2332 0%, #0a0e1a 100%)',
        borderRadius: '20px',
        padding: '24px',
        maxWidth: '400px',
        width: '100%',
        border: '1px solid rgba(59, 130, 246, 0.3)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
        }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700 }}>Select Payment Method</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#fff',
              fontSize: '24px',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            ×
          </button>
        </div>

        {/* Asset Name */}
        <div style={{
          textAlign: 'center',
          marginBottom: '20px',
          padding: '12px',
          background: 'rgba(59, 130, 246, 0.1)',
          borderRadius: '12px',
        }}>
          <div style={{ fontSize: '16px', fontWeight: 600, color: '#3b82f6' }}>
            {assetName}
          </div>
        </div>

        {/* Currency Options */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
          {/* USDT Option */}
          <button
            onClick={() => setSelectedCurrency('usdt')}
            disabled={!canAffordUsdt}
            style={{
              padding: '16px',
              background: selectedCurrency === 'usdt'
                ? 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)'
                : 'rgba(27, 94, 32, 0.2)',
              border: selectedCurrency === 'usdt' ? '2px solid #2e7d32' : '2px solid rgba(46, 125, 50, 0.2)',
              borderRadius: '12px',
              color: '#fff',
              cursor: canAffordUsdt ? 'pointer' : 'not-allowed',
              opacity: canAffordUsdt ? 1 : 0.5,
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                fontWeight: 'bold',
              }}>
                ₮
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: '16px', fontWeight: 600 }}>USDT</div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>
                  Balance: {usdtBalance.toFixed(2)}
                </div>
              </div>
            </div>
            <div style={{ fontSize: '18px', fontWeight: 700 }}>
              {priceUsdt.toFixed(2)}
            </div>
          </button>

          {/* Telegram Stars Option - Only show if allowed */}
          {allowStars && (
            <button
              onClick={() => setSelectedCurrency('stars')}
              style={{
                padding: '16px',
                background: selectedCurrency === 'stars'
                  ? 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)'
                  : 'rgba(30, 58, 138, 0.2)',
                border: selectedCurrency === 'stars' ? '2px solid #3b82f6' : '2px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '12px',
                color: '#fff',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                }}>
                  ⭐
                </div>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontSize: '16px', fontWeight: 600 }}>Telegram Stars</div>
                  <div style={{ fontSize: '12px', opacity: 0.8 }}>
                    Pay with Telegram
                  </div>
                </div>
              </div>
              <div style={{ fontSize: '18px', fontWeight: 700 }}>
                {priceStars} ⭐
              </div>
            </button>
          )}
        </div>

        {/* Purchase Button */}
        <button
          onClick={handlePurchase}
          disabled={selectedCurrency === 'usdt' && !canAffordUsdt}
          style={{
            width: '100%',
            padding: '16px',
            background: (selectedCurrency === 'usdt' && !canAffordUsdt)
              ? 'rgba(59, 130, 246, 0.3)'
              : 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
            border: 'none',
            borderRadius: '12px',
            color: '#fff',
            fontSize: '18px',
            fontWeight: 700,
            cursor: (selectedCurrency === 'usdt' && !canAffordUsdt) ? 'not-allowed' : 'pointer',
            boxShadow: (selectedCurrency === 'usdt' && !canAffordUsdt)
              ? 'none'
              : '0 4px 20px rgba(16, 185, 129, 0.4)',
          }}
        >
          {(selectedCurrency === 'usdt' && !canAffordUsdt)
            ? 'Insufficient Balance'
            : 'Purchase'}
        </button>
      </div>
    </div>
  );
}
